import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import AuthModal from "@/components/AuthModal";
import ThemeToggle from "@/components/ThemeToggle";
import { Logo } from "@/components/Logo";
import LoadingSpinner from "@/components/LoadingSpinner";
import MobileNav from "@/components/navigation/MobileNav";
import FloatingShapes from "@/components/3d/FloatingShapes";
import Hero3DScene from "@/components/3d/Hero3DScene";
import SEOHead from "@/components/SEOHead";
import StructuredData, { organizationSchema, softwareAppSchema, faqSchema, reviewSchema } from "@/components/StructuredData";
import { 
  Sparkles, 
  Image as ImageIcon, 
  Mic, 
  Zap,
  Crown,
  Star,
  ArrowRight,
  Play,
  Users,
  TrendingUp,
  Check,
  Palette,
  Wand2,
  Headphones,
  Rocket,
  Shield,
  Globe
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

const Index = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [authModal, setAuthModal] = useState<'login' | 'register' | null>(null);

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      setAuthModal('register');
    }
  };

  const handleTryNow = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      setAuthModal('register');
    }
  };

  const features = [
    {
      icon: <Palette className="w-8 h-8 text-white" />,
      title: "AI Image Creation",
      description: "Generate stunning visuals with advanced FLUX models and artistic styles",
      gradient: "from-purple-500 via-pink-500 to-rose-500",
      benefits: ["Ultra HD Quality", "Multiple Art Styles", "Instant Generation"],
      popular: true
    },
    {
      icon: <Headphones className="w-8 h-8 text-white" />,
      title: "AI Voice Synthesis", 
      description: "Create natural-sounding voiceovers in multiple languages with emotion",
      gradient: "from-blue-500 via-cyan-500 to-teal-500",
      benefits: ["Natural Voices", "Multi-language", "Emotion Control"]
    },
    {
      icon: <Wand2 className="w-8 h-8 text-white" />,
      title: "AI Script Writing",
      description: "Generate engaging content and scripts powered by advanced AI models",
      gradient: "from-emerald-500 via-green-500 to-lime-500",
      benefits: ["Creative Content", "SEO Optimized", "Multi-format"]
    }
  ];

  const stats = [
    { number: "1M+", label: "Images Created", icon: <ImageIcon className="w-5 h-5" />, color: "text-purple-500" },
    { number: "250K+", label: "Active Users", icon: <Users className="w-5 h-5" />, color: "text-blue-500" },
    { number: "99.9%", label: "Uptime", icon: <Zap className="w-5 h-5" />, color: "text-emerald-500" },
    { number: "4.9★", label: "User Rating", icon: <Star className="w-5 h-5" />, color: "text-yellow-500" }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Creative Director",
      company: "PixelCraft Studios",
      content: "Genova AI has revolutionized our creative workflow. The quality of generated content is absolutely stunning.",
      avatar: "SC",
      rating: 5
    },
    {
      name: "Marcus Johnson",
      role: "Marketing Manager",
      company: "TechFlow Inc",
      content: "The voice synthesis feature saved us thousands in production costs. Incredible technology!",
      avatar: "MJ",
      rating: 5
    },
    {
      name: "Elena Rodriguez",
      role: "Content Creator",
      company: "Digital Dreams",
      content: "From concept to creation in minutes. This AI platform is a game-changer for content creators.",
      avatar: "ER",
      rating: 5
    }
  ];

  // Enhanced loading screen with professional animations
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center relative overflow-hidden">
        {/* Premium animated background with reduced particles for performance */}
        <div className="absolute inset-0 premium-gradient">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-white/20 rounded-full animate-particle-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>
        
        {/* Epic loading animation */}
        <div className="text-center space-y-8 z-10">
          <div className="animate-epic-glow animate-morph">
            <Logo size="xl" />
          </div>
          <div className="space-y-6">
            <div className="text-2xl font-black text-white animate-text-glow">
              Genova AI Studio
            </div>
            <div className="text-purple-200 font-semibold animate-fade-in-up">
              Preparing your AI creative workspace...
            </div>
            <div className="flex space-x-2 justify-center">
              {[0, 1, 2].map((i) => (
                <div 
                  key={i}
                  className="w-3 h-3 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full animate-loading-pulse" 
                  style={{ animationDelay: `${i * 0.2}s` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const homeFaqs = [
    { question: 'What is Genova AI Studio?', answer: 'Genova AI Studio is an AI-powered content creation platform that lets you generate stunning images, realistic voiceovers, and engaging scripts using advanced artificial intelligence technology.' },
    { question: 'Is Genova AI Studio free to use?', answer: 'Yes! Genova AI Studio offers a free plan with 5 monthly generation credits. You can upgrade to Starter, Pro, or Business plans for more credits and features.' },
    { question: 'What types of AI content can I create?', answer: 'You can create AI-generated images in multiple art styles, realistic voiceovers in multiple languages including English, Hindi, and Tamil, and engaging scripts for YouTube, social media, and marketing.' },
    { question: 'What image quality does Genova AI support?', answer: 'Free users get 512px images. Paid plans support up to 4K Ultra HD (2048px) resolution with no watermarks on Pro and Business plans.' },
    { question: 'How does AI voice synthesis work?', answer: 'Our AI voice synthesis uses advanced text-to-speech technology to create natural-sounding voiceovers in multiple languages with emotion control.' },
  ];

  const homeReviews = [
    { author: 'Sarah Chen', rating: 5, body: 'Genova AI has revolutionized our creative workflow. The quality of generated content is absolutely stunning.' },
    { author: 'Marcus Johnson', rating: 5, body: 'The voice synthesis feature saved us thousands in production costs. Incredible technology!' },
    { author: 'Elena Rodriguez', rating: 5, body: 'From concept to creation in minutes. This AI platform is a game-changer for content creators.' },
  ];

  return (
    <>
      <SEOHead
        title="Genova AI Studio - #1 AI Content Creation Platform | AI Image, Voice & Script Generator"
        description="Create stunning AI images, realistic voiceovers & engaging scripts with Genova AI Studio. Free AI content creation tool trusted by 250K+ creators. Start free today!"
        keywords="AI content creation, AI image generator, AI voice generator, AI script writer, text to image, AI art generator, free AI tool, content creation platform, text to speech AI, AI writing tool, generate images with AI, best AI content creator"
        canonicalUrl="https://genova-ai-studio.lovable.app/"
      />
      <StructuredData data={organizationSchema} id="org-schema" />
      <StructuredData data={softwareAppSchema} id="app-schema" />
      <StructuredData data={faqSchema(homeFaqs)} id="faq-schema" />
      <StructuredData data={reviewSchema(homeReviews)} id="review-schema" />
    <ScrollArea className="min-h-screen" hideScrollbar>
      <div className="bg-gradient-to-br from-slate-50 via-white to-purple-50 dark:from-slate-950 dark:via-slate-900 dark:to-purple-950 relative overflow-hidden">
        {/* Enhanced Background Animation */}
        <div className="absolute inset-0 premium-gradient opacity-30"></div>

        {/* Optimized Floating Elements */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className={`absolute w-4 h-4 rounded-full animate-particle-float opacity-20 ${
                i % 3 === 0 ? 'bg-purple-400' : i % 3 === 1 ? 'bg-pink-400' : 'bg-blue-400'
              }`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 4}s`,
                animationDuration: `${4 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>

        {/* 3D Background Elements */}
        <FloatingShapes />

        {/* Enhanced Navigation */}
        <nav className="sticky top-0 z-50 glass-effect border-b border-purple-200/30 dark:border-purple-800/30 shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MobileNav onAuthClick={setAuthModal} />
                <div className="animate-bounce-in">
                  <Logo size="md" />
                </div>
              </div>

              <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
                <Link 
                  to="/pricing" 
                  className="text-slate-600 dark:text-slate-300 hover:text-purple-600 dark:hover:text-purple-400 transition-all duration-300 font-semibold hover-lift px-3 lg:px-4 py-2 rounded-xl hover:bg-purple-50 dark:hover:bg-purple-950/30"
                >
                  Pricing
                </Link>
                <Link 
                  to="/dashboard" 
                  className="text-slate-600 dark:text-slate-300 hover:text-purple-600 dark:hover:text-purple-400 transition-all duration-300 font-semibold hover-lift px-3 lg:px-4 py-2 rounded-xl hover:bg-purple-50 dark:hover:bg-purple-950/30"
                >
                  Features
                </Link>
                <ThemeToggle />
              </div>

              <div className="flex items-center space-x-2 sm:space-x-4">
                {user ? (
                  <div className="flex items-center space-x-2 sm:space-x-4 animate-fade-in">
                    <span className="hidden lg:block text-sm text-slate-600 dark:text-slate-300 font-medium">
                      Welcome, <span className="text-purple-600 dark:text-purple-400 font-bold animate-text-glow">{user.name}!</span>
                    </span>
                    <Button 
                      onClick={() => navigate('/dashboard')} 
                      size="sm"
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg hover:shadow-2xl hover-lift neon-border"
                    >
                      <Sparkles className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Dashboard</span>
                    </Button>
                  </div>
                ) : (
                  <div className="hidden md:flex items-center space-x-3 animate-fade-in">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setAuthModal('login')} 
                      className="font-semibold hover:bg-purple-50 dark:hover:bg-purple-950/20 transition-all duration-300 rounded-xl px-4 hover-lift"
                    >
                      Sign In
                    </Button>
                    <Button 
                      onClick={handleGetStarted} 
                      size="sm"
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg hover:shadow-2xl hover-lift neon-border"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Get Started
                    </Button>
                  </div>
                )}
                <div className="md:hidden">
                  <ThemeToggle />
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Enhanced Hero Section */}
        <header className="relative px-4 sm:px-6 py-12 sm:py-16 md:py-20 overflow-hidden" role="banner">
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Text content */}
              <div className="text-center lg:text-left order-2 lg:order-1">
                <div className="animate-bounce-in">
                  <Badge className="mb-6 sm:mb-8 glass-effect text-foreground border-border px-4 sm:px-8 py-2 sm:py-4 font-bold shadow-2xl text-sm sm:text-base hover-lift neon-border bg-card/80 inline-flex">
                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 text-primary" />
                    Next-Gen AI Creative Studio
                  </Badge>
                </div>
                
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-6 sm:mb-8 leading-tight">
                  Create Stunning AI Content with
                  <span className="block bg-gradient-to-r from-purple-600 via-pink-500 to-blue-500 bg-clip-text text-transparent mt-2 sm:mt-4 animate-gradient-shift">
                    Genova AI Studio
                  </span>
                </h1>
                
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-slate-600 dark:text-slate-300 mb-8 sm:mb-12 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-medium">
                  The #1 AI content creation platform. Generate professional AI images, realistic voiceovers, and SEO-optimized scripts instantly. Trusted by 250K+ creators worldwide.
                </p>

                <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 sm:gap-6 mb-8 lg:mb-0">
                  <Button 
                    size="lg" 
                    onClick={handleGetStarted}
                    className="bg-gradient-to-r from-purple-600 via-pink-500 to-blue-500 hover:from-purple-700 hover:via-pink-600 hover:to-blue-600 text-white text-base sm:text-lg px-8 sm:px-12 py-6 sm:py-8 rounded-full shadow-2xl hover:shadow-purple-500/25 font-black w-full sm:w-auto hover-lift neon-border"
                  >
                    <Rocket className="mr-2 sm:mr-4 w-5 h-5 sm:w-7 sm:h-7" />
                    Start Creating Free
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    onClick={handleTryNow}
                    className="border-2 border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950/30 text-base sm:text-lg px-8 sm:px-12 py-6 sm:py-8 rounded-full shadow-lg hover:shadow-2xl font-bold w-full sm:w-auto hover-lift glass-effect"
                  >
                    <Play className="mr-2 sm:mr-4 w-5 h-5 sm:w-7 sm:h-7" />
                    Watch Demo
                  </Button>
                </div>
              </div>

              {/* 3D Scene */}
              <div className="order-1 lg:order-2">
                <Hero3DScene />
              </div>
            </div>

            {/* Enhanced Animated Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mt-12 sm:mt-16 lg:mt-20">
              {stats.map((stat, index) => (
                <div 
                  key={index} 
                  className="text-center group animate-card-entrance hover-lift cursor-pointer"
                  style={{ animationDelay: `${0.7 + index * 0.1}s` }}
                >
                  <div className="glass-effect rounded-3xl p-8 shadow-2xl hover:shadow-purple-500/20 border border-purple-200/30 dark:border-purple-700/30 neon-border animate-morph">
                    <div className={`flex items-center justify-center mb-4 ${stat.color} group-hover:scale-125 transition-transform duration-500 animate-float`}>
                      {stat.icon}
                    </div>
                    <div className="text-4xl md:text-5xl font-black bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent mb-3 animate-counter">
                      {stat.number}
                    </div>
                    <div className="text-base font-bold text-slate-600 dark:text-slate-300">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </header>

        {/* Enhanced Features Section */}
        <main>
        <section className="px-6 py-20 premium-gradient" aria-label="AI Content Creation Features">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-20 animate-fade-in-up">
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-black mb-8">
                Powerful AI Tools for
                <span className="block bg-gradient-to-r from-purple-600 via-pink-500 to-blue-500 bg-clip-text text-transparent mt-2 animate-gradient-shift">
                  AI Content Creation
                </span>
              </h2>
              <p className="text-2xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto font-bold">
                Generate AI images, voiceovers, and scripts — everything you need for professional content creation
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-10 mb-16">
              {features.map((feature, index) => (
                <Card 
                  key={index} 
                  className="group relative overflow-hidden glass-effect border-purple-200/30 dark:border-purple-700/30 shadow-2xl hover:shadow-purple-500/20 hover-lift animate-card-entrance neon-border"
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  {feature.popular && (
                    <Badge className="absolute top-6 right-6 bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-bold shadow-lg animate-bounce-gentle z-10 neon-border">
                      <Star className="w-4 h-4 mr-2 animate-bounce-gentle" />
                      Popular
                    </Badge>
                  )}
                  <CardHeader className="text-center pb-8">
                    <div className={`w-24 h-24 mx-auto mb-8 bg-gradient-to-r ${feature.gradient} rounded-3xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-2xl animate-float animate-epic-glow`}>
                      {feature.icon}
                    </div>
                    <CardTitle className="text-3xl font-black mb-6 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-300 animate-text-glow">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-center space-y-8">
                    <p className="text-slate-600 dark:text-slate-300 leading-relaxed font-semibold text-lg">
                      {feature.description}
                    </p>
                    <div className="space-y-4">
                      {feature.benefits.map((benefit, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-center justify-center space-x-4 text-base group-hover:scale-105 transition-transform duration-300 animate-fade-in"
                          style={{ animationDelay: `${idx * 0.1}s` }}
                        >
                          <div className="w-6 h-6 bg-gradient-to-r from-emerald-400 to-green-500 rounded-full flex items-center justify-center animate-bounce-gentle shadow-lg">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-slate-600 dark:text-slate-300 font-bold">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Button 
                size="lg" 
                onClick={handleTryNow}
                className="bg-gradient-to-r from-purple-600 via-pink-500 to-blue-500 hover:from-purple-700 hover:via-pink-600 hover:to-blue-600 text-white text-xl px-12 py-8 rounded-full shadow-2xl hover:shadow-purple-500/25 font-black hover-lift neon-border animate-epic-glow"
              >
                Try All Features <ArrowRight className="ml-4 w-7 h-7 animate-bounce-gentle" />
              </Button>
            </div>
          </div>
        </section>

        {/* Enhanced Testimonials Section */}
        <section className="px-6 py-20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-20 animate-fade-in-up">
              <h2 className="text-4xl sm:text-5xl font-black mb-8">
                Loved by <span className="bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent animate-gradient-shift">Creators Worldwide</span>
              </h2>
              <p className="text-2xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto font-semibold">
                See what our community says about their experience with Genova AI Studio
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-10 mb-16">
              {testimonials.map((testimonial, index) => (
                <Card 
                  key={index} 
                  className="glass-effect border-purple-200/30 dark:border-purple-700/30 shadow-2xl hover:shadow-purple-500/20 hover-lift animate-card-entrance neon-border"
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  <CardContent className="p-8">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-xl mr-6 animate-float shadow-lg">
                        {testimonial.avatar}
                      </div>
                      <div>
                        <div className="font-black text-lg text-slate-800 dark:text-white">{testimonial.name}</div>
                        <div className="text-sm text-slate-600 dark:text-slate-300 font-semibold">{testimonial.role}</div>
                        <div className="text-sm text-purple-600 dark:text-purple-400 font-bold">{testimonial.company}</div>
                      </div>
                    </div>
                    <div className="flex mb-6">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-yellow-400 fill-current animate-bounce-gentle" style={{ animationDelay: `${i * 0.1}s` }} />
                      ))}
                    </div>
                    <p className="text-slate-600 dark:text-slate-300 italic leading-relaxed text-lg font-medium">
                      "{testimonial.content}"
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Preview */}
        <section className="px-6 py-20 bg-gradient-to-r from-purple-50/50 via-white/50 to-pink-50/50 dark:from-purple-950/20 dark:via-slate-900/50 dark:to-pink-950/20 backdrop-blur-xl">
          <div className="max-w-6xl mx-auto text-center">
            <div className="animate-fade-in-up mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 px-2">
                Choose Your
                <span className="block bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent mt-1 sm:mt-2 animate-gradient-shift">
                  Creative Plan
                </span>
              </h2>
              <p className="text-lg sm:text-xl text-slate-600 dark:text-slate-300 mb-8 sm:mb-12 max-w-3xl mx-auto font-medium px-4">
                Start free and scale as your creative needs grow. All plans include access to our core AI features.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {/* Free Plan */}
              <Card className="border-2 border-purple-200/50 dark:border-purple-700/50 hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-300 hover:shadow-xl bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl animate-card-entrance transform hover:scale-105">
                <CardHeader className="text-center">
                  <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg animate-float">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-2xl font-bold">Free</CardTitle>
                  <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">₹0</div>
                  <p className="text-slate-600 dark:text-slate-300 font-medium">forever</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    "5 generation credits per month",
                    "Standard quality",
                    "Basic support",
                    "Genova AI watermark"
                  ].map((feature, idx) => (
                    <div key={idx} className="text-sm flex items-center space-x-2 animate-fade-in" style={{ animationDelay: `${idx * 0.1}s` }}>
                      <Check className="w-4 h-4 text-green-500 animate-pulse" />
                      <span className="font-medium text-slate-600 dark:text-slate-300">{feature}</span>
                    </div>
                  ))}
                  <Button onClick={handleGetStarted} variant="outline" className="w-full mt-4 hover:bg-blue-50 dark:hover:bg-blue-950/20 font-semibold border-2 border-blue-200 dark:border-blue-700 hover:border-blue-300 dark:hover:border-blue-600 transform hover:scale-105 transition-all duration-300">
                    Get Started Free
                  </Button>
                </CardContent>
              </Card>

              {/* Pro Plan */}
              <Card className="border-2 border-purple-400 dark:border-purple-600 relative scale-105 shadow-2xl bg-gradient-to-b from-purple-50/80 to-pink-50/80 dark:from-purple-950/40 dark:to-pink-950/40 backdrop-blur-xl animate-pulse-glow">
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-lg font-bold animate-bounce">
                  <Star className="w-3 h-3 mr-1 animate-spin" />
                  Most Popular
                </Badge>
                <CardHeader className="text-center">
                  <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg animate-float">
                    <Crown className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-2xl font-bold">Pro</CardTitle>
                  <div className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent">₹1,199</div>
                  <p className="text-slate-600 dark:text-slate-300 font-medium">per month</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    "100 generation credits per month",
                    "HD quality (up to 1536px)",
                    "No watermarks",
                    "Priority support",
                    "Multiple art styles"
                  ].map((feature, idx) => (
                    <div key={idx} className="text-sm flex items-center space-x-2 animate-fade-in" style={{ animationDelay: `${idx * 0.1}s` }}>
                      <Check className="w-4 h-4 text-green-500 animate-pulse" />
                      <span className="font-medium text-slate-600 dark:text-slate-300">{feature}</span>
                    </div>
                  ))}
                  <Button onClick={() => navigate('/pricing')} className="w-full mt-4 bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 shadow-lg font-bold transform hover:scale-105 transition-all duration-300">
                    Start Pro Plan
                  </Button>
                </CardContent>
              </Card>

              {/* Team Plan */}
              <Card className="border-2 border-orange-200/50 dark:border-orange-700/50 hover:border-orange-300 dark:hover:border-orange-600 transition-all duration-300 hover:shadow-xl bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl animate-card-entrance transform hover:scale-105" style={{ animationDelay: '0.4s' }}>
                <CardHeader className="text-center">
                  <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center shadow-lg animate-float">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-2xl font-bold">Team</CardTitle>
                  <div className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">₹2,499</div>
                  <p className="text-slate-600 dark:text-slate-300 font-medium">per month</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    "300 generation credits per month",
                    "4K Ultra HD (2048px)",
                    "Team collaboration",
                    "Advanced analytics",
                    "Custom branding"
                  ].map((feature, idx) => (
                    <div key={idx} className="text-sm flex items-center space-x-2 animate-fade-in" style={{ animationDelay: `${idx * 0.1}s` }}>
                      <Check className="w-4 h-4 text-green-500 animate-pulse" />
                      <span className="font-medium text-slate-600 dark:text-slate-300">{feature}</span>
                    </div>
                  ))}
                  <Button onClick={() => navigate('/pricing')} variant="outline" className="w-full mt-4 hover:bg-orange-50 dark:hover:bg-orange-950/20 font-semibold border-2 border-orange-200 dark:border-orange-700 hover:border-orange-300 dark:hover:border-orange-600 transform hover:scale-105 transition-all duration-300">
                    Start Team Plan
                  </Button>
                </CardContent>
              </Card>
            </div>

            <Link to="/pricing" className="inline-flex items-center text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 font-medium transition-colors hover:scale-105 transform duration-200 text-sm sm:text-base animate-fade-in">
              View detailed pricing <ArrowRight className="ml-2 w-3 h-3 sm:w-4 sm:h-4" />
            </Link>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="px-6 py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 via-pink-500/10 to-blue-600/10 animate-gradient-shift"></div>
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <div className="animate-bounce-in">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black mb-8">
                Ready to Create Amazing Content?
              </h2>
              <p className="text-xl text-slate-600 dark:text-slate-300 mb-12 max-w-2xl mx-auto font-semibold">
                Join thousands of creators who are already using Genova AI Studio to bring their ideas to life with professional AI-powered tools.
              </p>
              <Button 
                size="lg" 
                onClick={handleGetStarted}
                className="bg-gradient-to-r from-purple-600 via-pink-500 to-blue-500 hover:from-purple-700 hover:via-pink-600 hover:to-blue-600 text-white text-lg px-10 py-6 rounded-full shadow-2xl hover:shadow-purple-500/25 font-black transform hover:scale-110 transition-all duration-500 animate-pulse-glow"
              >
                <Sparkles className="mr-3 w-6 h-6 animate-spin" />
                Start Creating Free
              </Button>
            </div>
          </div>
        </section>
        </main>

        {/* SEO Footer */}
        <footer className="px-6 py-16 border-t border-purple-200/30 dark:border-purple-800/30 bg-slate-50/80 dark:bg-slate-950/80">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
              <div>
                <Logo size="md" />
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-4 leading-relaxed">
                  Genova AI Studio is the leading AI content creation platform. Generate professional AI images, realistic voiceovers, and engaging scripts instantly.
                </p>
              </div>
              <div>
                <h3 className="font-bold text-slate-800 dark:text-slate-200 mb-4">AI Features</h3>
                <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                  <li><Link to="/dashboard" className="hover:text-purple-600 transition-colors">AI Image Generator</Link></li>
                  <li><Link to="/dashboard" className="hover:text-purple-600 transition-colors">AI Voice Synthesis</Link></li>
                  <li><Link to="/dashboard" className="hover:text-purple-600 transition-colors">AI Script Writer</Link></li>
                  <li><Link to="/dashboard" className="hover:text-purple-600 transition-colors">Text to Image AI</Link></li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-slate-800 dark:text-slate-200 mb-4">Plans</h3>
                <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                  <li><Link to="/pricing" className="hover:text-purple-600 transition-colors">Free Plan</Link></li>
                  <li><Link to="/pricing" className="hover:text-purple-600 transition-colors">Starter Plan - ₹499/mo</Link></li>
                  <li><Link to="/pricing" className="hover:text-purple-600 transition-colors">Pro Plan - ₹1,199/mo</Link></li>
                  <li><Link to="/pricing" className="hover:text-purple-600 transition-colors">Business Plan - ₹2,499/mo</Link></li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-slate-800 dark:text-slate-200 mb-4">Use Cases</h3>
                <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                  <li>Social Media Content</li>
                  <li>YouTube Video Scripts</li>
                  <li>Marketing Materials</li>
                  <li>Podcast Voiceovers</li>
                </ul>
              </div>
            </div>
            <div className="border-t border-purple-200/30 dark:border-purple-800/30 pt-8 text-center">
              <p className="text-sm text-slate-500 dark:text-slate-500">
                © {new Date().getFullYear()} Genova AI Studio. AI Content Creation Platform — Generate Images, Voice & Scripts with AI.
              </p>
            </div>
          </div>
        </footer>

        {authModal && (
          <AuthModal 
            mode={authModal} 
            onClose={() => setAuthModal(null)}
            onModeChange={setAuthModal}
          />
        )}
      </div>
    </ScrollArea>
    </>
  );
};

export default Index;
